import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { toCSV, downloadCSV } from "@/lib/csv";
import { useSortFilter, SortableTh, SearchBox } from "@/lib/tableUtils";
import { PageHeader, Card, Empty, Field, Input, Btn } from "@/components/UI";

export default function StockPage() {
  const [list, setList] = useState([]);
  const [adjustFor, setAdjustFor] = useState(null); // { row, history }

  const load = () => api.get("/stock").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const { q, setQ, sort, toggleSort, rows } = useSortFilter(list, {
    searchKeys: ["style_name", "sku", "size", "color"],
  });

  const total = list.reduce((a, b) => a + Math.max(0, b.stock_pcs || 0), 0);
  const totalBoxes = list.reduce((a, b) => a + Math.max(0, b.stock_boxes || 0), 0);
  const totalLooseStock = list.reduce((a, b) => a + Math.max(0, b.loose_stock_pcs || 0), 0);
  const wipStitch = list.reduce((a, b) => a + Math.max(0, b.wip_stitch || 0), 0);
  const wipPack = list.reduce((a, b) => a + Math.max(0, b.wip_pack || 0), 0);

  const exportCSV = () => {
    const columns = [
      { key: "style_name", label: "Style" },
      { key: "sku", label: "SKU" },
      { key: "size", label: "Size" },
      { key: "color", label: "Color" },
      { key: "pieces_per_box", label: "Pcs/Box" },
      { key: "cut_pcs", label: "Cut" },
      { key: "stitched_pcs", label: "Stitched" },
      { key: "loose_pcs", label: "Loose WIP (Pcs)" },
      { key: "loose_stock_pcs", label: "Loose Stock (Pcs · Manual)" },
      { key: "packed_pcs", label: "Packed (Pcs)" },
      { key: "packed_boxes", label: "Packed (Box)" },
      { key: "dispatched_pcs", label: "Dispatched (Pcs)" },
      { key: "dispatched_boxes", label: "Dispatched (Box)" },
      { key: "stock_pcs", label: "Stock (Pcs)" },
      { key: "stock_boxes", label: "Stock (Box)" },
      { key: "reorder_level", label: "Reorder Level" },
    ];
    downloadCSV(`slkc-stock-${new Date().toISOString().slice(0, 10)}.csv`, toCSV(list, columns));
  };

  const openAdjust = async (row) => {
    try {
      const { data } = await api.get(`/loose-stock`, { params: { product_id: row.id } });
      setAdjustFor({ row, history: data });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader
        label="Stage 08"
        title="Finished Stock"
        subtitle="Auto-computed: Packed − Dispatched (per SKU) · Loose Pcs = physical inventory of unboxed pcs, manually adjustable"
        right={
          <button
            onClick={exportCSV}
            data-testid="stock-export-csv"
            className="h-9 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold bg-foreground text-background hover:-translate-y-[1px] transition-transform"
          >
            ↓ Export CSV
          </button>
        }
      />
      <div className="p-4 md:p-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-white border border-border p-4">
          <div className="label-caps">Finished Stock</div>
          <div className="font-display font-black text-4xl mt-2">{total.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground font-mono mt-1">{totalBoxes.toLocaleString()} boxes</div>
        </div>
        <div className="bg-white border border-border p-4">
          <div className="label-caps">Loose Pcs Stock</div>
          <div className="font-display font-black text-4xl mt-2">{totalLooseStock.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground font-mono mt-1">manually tracked · pcs</div>
        </div>
        <div className="bg-white border border-border p-4">
          <div className="label-caps">Stitching WIP</div>
          <div className="font-display font-black text-4xl mt-2">{wipStitch.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground font-mono mt-1">cut but not stitched</div>
        </div>
        <div className="bg-white border border-border p-4">
          <div className="label-caps">Packing WIP</div>
          <div className="font-display font-black text-4xl mt-2">{wipPack.toLocaleString()}</div>
          <div className="text-xs text-muted-foreground font-mono mt-1">stitched but not packed</div>
        </div>
      </div>
      <div className="px-4 md:px-8 pb-8 md:pb-12">
        <Card title={`Stock by SKU · ${rows.length}${rows.length !== list.length ? ` of ${list.length}` : ""}`} right={
          <SearchBox value={q} onChange={setQ} placeholder="Search style, SKU, size, color…" testid="stock-search" />
        }>
          <div className="table-scroll hidden md:block" data-testid="stock-scroll">
            <table className="table-dense w-full">
              <thead>
                <tr>
                  <SortableTh label="Style" sortKey="style_name" sort={sort} toggleSort={toggleSort} />
                  <SortableTh label="SKU" sortKey="sku" sort={sort} toggleSort={toggleSort} />
                  <SortableTh label="Size" sortKey="size" sort={sort} toggleSort={toggleSort} />
                  <SortableTh label="Color" sortKey="color" sort={sort} toggleSort={toggleSort} />
                  <SortableTh label="Pcs/Box" sortKey="pieces_per_box" sort={sort} toggleSort={toggleSort} className="text-right" />
                  <SortableTh label="Cut" sortKey="cut_pcs" sort={sort} toggleSort={toggleSort} className="text-right" />
                  <SortableTh label="Stitched" sortKey="stitched_pcs" sort={sort} toggleSort={toggleSort} className="text-right" />
                  <SortableTh label="Loose WIP" sortKey="loose_pcs" sort={sort} toggleSort={toggleSort} className="text-right" />
                  <SortableTh label="Loose Stock" sortKey="loose_stock_pcs" sort={sort} toggleSort={toggleSort} className="text-right" />
                  <SortableTh label="Packed Pcs" sortKey="packed_pcs" sort={sort} toggleSort={toggleSort} className="text-right" />
                  <SortableTh label="Packed Box" sortKey="packed_boxes" sort={sort} toggleSort={toggleSort} className="text-right" />
                  <SortableTh label="Disp Pcs" sortKey="dispatched_pcs" sort={sort} toggleSort={toggleSort} className="text-right" />
                  <SortableTh label="Disp Box" sortKey="dispatched_boxes" sort={sort} toggleSort={toggleSort} className="text-right" />
                  <SortableTh label="Stock Pcs" sortKey="stock_pcs" sort={sort} toggleSort={toggleSort} className="text-right" />
                  <SortableTh label="Stock Box" sortKey="stock_boxes" sort={sort} toggleSort={toggleSort} className="text-right" />
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} data-testid={`stock-${s.sku}-${s.size}-${s.color}`}>
                    <td>{s.style_name}</td>
                    <td className="font-mono">{s.sku}</td>
                    <td>{s.size}</td>
                    <td>{s.color}</td>
                    <td className="tabular text-right text-muted-foreground">{s.pieces_per_box || "—"}</td>
                    <td className="tabular text-right">{s.cut_pcs}</td>
                    <td className="tabular text-right">{s.stitched_pcs}</td>
                    <td className={`tabular text-right ${(s.loose_pcs || 0) > 0 ? "text-muted-foreground" : "text-muted-foreground"}`}>{s.loose_pcs || 0}</td>
                    <td className={`tabular text-right font-bold ${(s.loose_stock_pcs || 0) > 0 ? "text-accent" : "text-muted-foreground"}`}>{s.loose_stock_pcs || 0}</td>
                    <td className="tabular text-right">{s.packed_pcs}</td>
                    <td className="tabular text-right">{s.packed_boxes || 0}</td>
                    <td className="tabular text-right">{s.dispatched_pcs}</td>
                    <td className="tabular text-right">{s.dispatched_boxes || 0}</td>
                    <td className={`tabular text-right font-bold ${s.stock_pcs > 0 ? "" : "text-muted-foreground"}`}>{s.stock_pcs}</td>
                    <td className={`tabular text-right font-bold ${(s.stock_boxes || 0) > 0 ? "" : "text-muted-foreground"}`}>{s.stock_boxes || 0}</td>
                    <td className="text-right">
                      <button
                        onClick={() => openAdjust(s)}
                        data-testid={`stock-adjust-loose-${s.id}`}
                        className="label-caps text-accent hover:underline"
                        title="Adjust manual loose-pcs stock (+/-)"
                      >
                        Adjust Loose
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={16}><Empty text={list.length ? "No SKUs match your search" : "No products defined yet"} /></td></tr>}
              </tbody>
            </table>
          </div>
          {/* Mobile: card-per-SKU — big touch targets, no horizontal scroll. */}
          <div className="md:hidden space-y-3">
            {rows.map((s) => {
              const label = [s.style_name, s.sku, s.size, s.color].filter(Boolean).join(" / ");
              return (
                <div key={s.id} className="border border-border bg-white p-3" data-testid={`stock-card-${s.id}`}>
                  <div className="font-semibold text-sm">{label}</div>
                  <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{s.pieces_per_box ? `${s.pieces_per_box} pcs / box` : "no box config"}</div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                    <div><div className="label-caps text-muted-foreground text-[9px]">Cut</div><div className="tabular">{s.cut_pcs}</div></div>
                    <div><div className="label-caps text-muted-foreground text-[9px]">Stitched</div><div className="tabular">{s.stitched_pcs}</div></div>
                    <div><div className="label-caps text-muted-foreground text-[9px]">Loose WIP</div><div className="tabular">{s.loose_pcs || 0}</div></div>
                    <div><div className="label-caps text-accent text-[9px]">Loose Stock</div><div className={`tabular font-bold ${(s.loose_stock_pcs || 0) > 0 ? "text-accent" : ""}`}>{s.loose_stock_pcs || 0}</div></div>
                    <div><div className="label-caps text-muted-foreground text-[9px]">Packed</div><div className="tabular">{s.packed_pcs} · {s.packed_boxes || 0}bx</div></div>
                    <div><div className="label-caps text-muted-foreground text-[9px]">Disp</div><div className="tabular">{s.dispatched_pcs} · {s.dispatched_boxes || 0}bx</div></div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                    <div>
                      <div className="label-caps text-[9px]">Stock</div>
                      <div className="tabular text-lg font-bold">{s.stock_pcs} <span className="text-xs text-muted-foreground">pcs · {s.stock_boxes || 0}bx</span></div>
                    </div>
                    <button
                      onClick={() => openAdjust(s)}
                      data-testid={`stock-adjust-loose-mobile-${s.id}`}
                      className="h-9 px-3 bg-foreground text-background font-mono text-[10px] tracking-[0.15em] uppercase font-bold"
                    >
                      Adjust Loose
                    </button>
                  </div>
                </div>
              );
            })}
            {rows.length === 0 && <Empty text={list.length ? "No SKUs match your search" : "No products defined yet"} />}
          </div>
        </Card>
      </div>
      {adjustFor && (
        <LooseAdjustModal
          row={adjustFor.row}
          history={adjustFor.history}
          onClose={() => setAdjustFor(null)}
          onSaved={() => { load(); setAdjustFor(null); }}
        />
      )}
    </div>
  );
}

function LooseAdjustModal({ row, history, onClose, onSaved }) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const current = Number(row.loose_stock_pcs || 0);
  const label = [row.style_name, row.sku, row.size, row.color].filter(Boolean).join(" / ");

  const submit = async (e) => {
    e.preventDefault();
    const d = parseInt(delta);
    if (!d || d === 0) return toast.error("Enter a non-zero adjustment (e.g. +12 or -3)");
    if (current + d < 0) return toast.error(`Cannot take stock negative (current ${current}, delta ${d})`);
    setSaving(true);
    try {
      await api.post("/loose-stock/adjust", { product_id: row.id, delta: d, reason });
      toast.success(`Loose stock updated · ${d > 0 ? "+" : ""}${d} pcs`);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="loose-adjust-modal">
      <div className="bg-white border border-border shadow-2xl w-full sm:max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-border">
          <div className="label-caps">Loose Pcs Stock · Adjust</div>
          <div className="font-display font-black text-xl mt-1">{label || "—"}</div>
          <div className="text-xs font-mono text-muted-foreground mt-1">Current physical loose stock: <b>{current}</b> pcs</div>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <Field label="Delta (+ to add, − to remove)">
            <Input type="number" step="1" required autoFocus value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="e.g. 12 or -3" data-testid="loose-adjust-delta" />
          </Field>
          <Field label="Reason / note">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="physical count, damaged, gift-away, spillover…" data-testid="loose-adjust-reason" />
          </Field>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-10 label-caps border border-border hover:bg-secondary">Cancel</button>
            <Btn data-testid="loose-adjust-submit" disabled={saving}>{saving ? "Saving…" : "Apply Adjustment"}</Btn>
          </div>
        </form>
        {history?.length > 0 && (
          <div className="border-t border-border max-h-56 overflow-auto">
            <div className="p-3 label-caps text-muted-foreground">Recent adjustments · {history.length}</div>
            <table className="table-dense w-full">
              <thead><tr><th>Date</th><th className="text-right">Δ</th><th>Reason</th><th>By</th></tr></thead>
              <tbody>
                {history.slice(0, 20).map((h) => (
                  <tr key={h.id}>
                    <td className="text-xs">{h.date}</td>
                    <td className={`tabular text-right font-bold ${h.delta > 0 ? "text-accent" : "text-destructive"}`}>{h.delta > 0 ? "+" : ""}{h.delta}</td>
                    <td className="text-xs">{h.reason || "—"}</td>
                    <td className="text-xs">{h.created_by_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
